import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X, Copy, CheckCircle2, DollarSign, Users, TrendingUp, FileText, Briefcase, Code, Building2, Wallet, Save, Edit2, Plus, Trash2, ImagePlus } from 'lucide-react';
import { cn } from '../lib/utils';
import { updateStartupDataInReporte } from '../services/apiService';

const DatosStartup = ({ startup, reporte, onClose, onCopiarDatos, onSave, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [estadoManual, setEstadoManual] = useState('pendiente'); // Estado manual del usuario
  
  // Estado para los datos editables
  const [formData, setFormData] = useState({
    kpis: {
      ingresos: '',
      gastos: '',
      empleados: '',
      runway: '',
      rondaAbierta: false,
      valoracion: '',
      progresoRonda: '',
      incorporation: '',
      totalInvertido: '',
      pctPortfolio: '',
      pctEquity: '',
      summary: '',
      web: '',
      exitValue: '',
      logo_url: ''
    },
    milestones: {
      'Comercial': [],
      'Producto': [],
      'Tecnología': [],
      'RRHH': [],
      'Financiero': [],
      'General': [],
      'Otros': []
    }
  });

  // Función para normalizar el nombre del área desde BD a formato del frontend
  const normalizarArea = (area) => {
    if (!area) return 'General';
    
    const areaLower = area.toLowerCase().trim();
    
    // Mapeo de áreas desde BD (minúsculas/variaciones) a formato frontend (mayúsculas)
    const mapeoAreas = {
      'comercial': 'Comercial',
      'producto': 'Producto',
      'tecnologia': 'Tecnología',
      'tecnología': 'Tecnología',
      'rrhh': 'RRHH',
      'recursos humanos': 'RRHH',
      'financiero': 'Financiero',
      'general': 'General',
      'otros': 'Otros'
    };
    
    return mapeoAreas[areaLower] || 'Otros';
  };

  // Inicializar datos desde startup.datos
  useEffect(() => {
    // Inicializar estado manual desde startup.estado
    if (startup.estado) {
      setEstadoManual(startup.estado);
    } else {
      setEstadoManual('pendiente');
    }
    
    if (startup.datos) {
      const kpis = startup.datos.kpis || {};
      const milestones = startup.datos.milestones || [];
      
      // Organizar milestones por área
      const milestonesByArea = {
        'Comercial': [],
        'Producto': [],
        'Tecnología': [],
        'RRHH': [],
        'Financiero': [],
        'General': [],
        'Otros': []
      };
      
      milestones.forEach(milestone => {
        let area = 'General';
        let texto = '';
        
        if (typeof milestone === 'object' && milestone.area && milestone.descripcion) {
          area = normalizarArea(milestone.area);
          texto = milestone.descripcion;
        } else if (typeof milestone === 'object' && milestone.area && milestone.texto) {
          area = normalizarArea(milestone.area);
          texto = milestone.texto;
        } else if (typeof milestone === 'string') {
          texto = milestone;
        } else if (typeof milestone === 'object') {
          area = normalizarArea(milestone.area || 'General');
          texto = milestone.texto || milestone.text || JSON.stringify(milestone);
        }
        
        // Normalizar el área antes de verificar
        const areaNormalizada = normalizarArea(area);
        if (milestonesByArea[areaNormalizada]) {
          milestonesByArea[areaNormalizada].push(texto);
        } else {
          milestonesByArea['Otros'].push(texto);
        }
      });
      
      setFormData({
        kpis: {
          ingresos: kpis.ingresos || '',
          gastos: kpis.gastos || '',
          empleados: kpis.empleados || '',
          runway: kpis.runway || '',
          rondaAbierta: kpis.rondaAbierta === true || kpis.rondaAbierta === 'true' || kpis.rondaAbierta === 'Sí',
          valoracion: kpis.valoracion || '',
          progresoRonda: kpis.progresoRonda || '',
          incorporation: kpis.incorporation || '',
          totalInvertido: kpis.totalInvertido || '',
          pctPortfolio: kpis.pctPortfolio || '',
          pctEquity: kpis.pctEquity || '',
          summary: kpis.summary || '',
          web: kpis.web || '',
          exitValue: kpis.exitValue || '',
          logo_url: kpis.logo_url || kpis.logoUrl || ''
        },
        milestones: milestonesByArea
      });
    }
  }, [startup.datos, startup.estado]);

  const areasHitos = {
    'Comercial': { icon: TrendingUp, color: 'text-blue-600' },
    'Producto': { icon: FileText, color: 'text-green-600' },
    'Tecnología': { icon: Code, color: 'text-purple-600' },
    'RRHH': { icon: Users, color: 'text-orange-600' },
    'Financiero': { icon: Wallet, color: 'text-yellow-600' },
    'General': { icon: Building2, color: 'text-gray-600' },
    'Otros': { icon: Briefcase, color: 'text-indigo-600' }
  };

  const handleSave = async () => {
    setGuardando(true);
    try {
      // Convertir milestones a formato array
      const milestonesArray = [];
      Object.keys(formData.milestones).forEach(area => {
        formData.milestones[area].forEach(texto => {
          if (texto.trim()) {
            milestonesArray.push({
              area: area,
              descripcion: texto.trim()
            });
          }
        });
      });

      // Preparar KPIs para guardar
      // IMPORTANTE: Si un campo está vacío, enviarlo como null explícitamente para que se elimine
      const kpisToSave = {
        ingresos: formData.kpis.ingresos && formData.kpis.ingresos.toString().trim() !== '' 
          ? (isNaN(formData.kpis.ingresos) ? formData.kpis.ingresos : parseFloat(formData.kpis.ingresos)) 
          : null,
        gastos: formData.kpis.gastos && formData.kpis.gastos.toString().trim() !== '' 
          ? (isNaN(formData.kpis.gastos) ? formData.kpis.gastos : parseFloat(formData.kpis.gastos)) 
          : null,
        empleados: formData.kpis.empleados && formData.kpis.empleados.toString().trim() !== '' 
          ? (isNaN(formData.kpis.empleados) ? parseInt(formData.kpis.empleados) : formData.kpis.empleados) 
          : null,
        runway: formData.kpis.runway && formData.kpis.runway.toString().trim() !== '' 
          ? (isNaN(formData.kpis.runway) ? parseInt(formData.kpis.runway) : formData.kpis.runway) 
          : null,
        rondaAbierta: formData.kpis.rondaAbierta,
        valoracion: formData.kpis.valoracion && formData.kpis.valoracion.toString().trim() !== '' 
          ? formData.kpis.valoracion 
          : null,
        progresoRonda: formData.kpis.progresoRonda && formData.kpis.progresoRonda.toString().trim() !== '' 
          ? formData.kpis.progresoRonda 
          : null,
        incorporation: formData.kpis.incorporation && formData.kpis.incorporation.toString().trim() !== '' ? formData.kpis.incorporation.trim() : null,
        totalInvertido: formData.kpis.totalInvertido && formData.kpis.totalInvertido.toString().trim() !== '' ? formData.kpis.totalInvertido.trim() : null,
        pctPortfolio: formData.kpis.pctPortfolio && formData.kpis.pctPortfolio.toString().trim() !== '' ? formData.kpis.pctPortfolio.trim() : null,
        pctEquity: formData.kpis.pctEquity && formData.kpis.pctEquity.toString().trim() !== '' ? formData.kpis.pctEquity.trim() : null,
        summary: formData.kpis.summary && formData.kpis.summary.toString().trim() !== '' ? formData.kpis.summary.trim() : null,
        web: formData.kpis.web && formData.kpis.web.toString().trim() !== '' ? formData.kpis.web.trim() : null,
        exitValue: formData.kpis.exitValue && formData.kpis.exitValue.toString().trim() !== '' ? formData.kpis.exitValue.trim() : null,
        logo_url: formData.kpis.logo_url && formData.kpis.logo_url.toString().trim() !== '' ? formData.kpis.logo_url.trim() : null
      };

      // Determinar estado basado en si todos los campos requeridos están completos
      // Enviar el estado manual seleccionado por el usuario
      await updateStartupDataInReporte(reporte.id, startup.id, {
        kpis: kpisToSave,
        milestones: milestonesArray,
        estado: estadoManual // Estado manual del usuario
      });

      setIsEditing(false);
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error guardando datos:', error);
      alert('Error al guardar los datos. Por favor, intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const handleAddMilestone = (area) => {
    setFormData(prev => ({
      ...prev,
      milestones: {
        ...prev.milestones,
        [area]: [...prev.milestones[area], '']
      }
    }));
  };

  const handleRemoveMilestone = (area, index) => {
    setFormData(prev => ({
      ...prev,
      milestones: {
        ...prev.milestones,
        [area]: prev.milestones[area].filter((_, i) => i !== index)
      }
    }));
  };

  const handleMilestoneChange = (area, index, value) => {
    setFormData(prev => ({
      ...prev,
      milestones: {
        ...prev.milestones,
        [area]: prev.milestones[area].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const handleCopiarDatos = () => {
    // Formatear datos para copiar al portapapeles
    const kpis = formData.kpis;
    const milestones = Object.keys(formData.milestones).flatMap(area => 
      formData.milestones[area].filter(texto => texto.trim()).map(texto => ({ area, texto }))
    );
    
    const texto = `
${startup.nombre}
Fundador: ${startup.fundador}
Arcángel: ${startup.arcangel}
Website: ${startup.website || 'No disponible'}

Descripción:
${startup.descripcion || 'Sin descripción disponible.'}

KPIs Financieros:
- Ingresos: ${kpis.ingresos || 'No disponible'}
- Gastos: ${kpis.gastos || 'No disponible'}
- Empleados: ${kpis.empleados || 'No disponible'}
- Runway: ${kpis.runway ? `${kpis.runway} meses` : 'No disponible'}
- Ronda abierta: ${kpis.rondaAbierta ? 'Sí' : 'No'}
${kpis.rondaAbierta && kpis.valoracion ? `- Valoración: ${kpis.valoracion}` : ''}
${kpis.rondaAbierta && kpis.progresoRonda ? `- Progreso: ${kpis.progresoRonda}${kpis.progresoRonda.toString().includes('%') ? '' : '%'}` : ''}

Hitos del Trimestre:
${milestones.map(hito => `• [${hito.area}] ${hito.texto}`).join('\n')}
    `.trim();

    navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
    
    if (onCopiarDatos) {
      onCopiarDatos({ kpis, milestones });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border/50 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold">{startup.nombre}</h2>
            <p className="text-muted-foreground text-sm">
              Reporte {reporte.nombre} • {startup.fundador}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCopiarDatos}
                  className={copiado ? 'bg-green-50 dark:bg-green-950' : ''}
                >
                  {copiado ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar Datos
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="default" 
                  onClick={handleSave}
                  disabled={guardando}
                >
                  {guardando ? (
                    <>
                      <Save className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(false)}
                  disabled={guardando}
                >
                  Cancelar
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Estado Manual */}
          <Card>
            <CardHeader>
              <CardTitle>Estado de la Startup</CardTitle>
              <CardDescription>
                Marca manualmente cuando la startup esté completa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="estado"
                      value="pendiente"
                      checked={estadoManual === 'pendiente'}
                      onChange={(e) => setEstadoManual(e.target.value)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Pendiente</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="estado"
                      value="incompleto"
                      checked={estadoManual === 'incompleto'}
                      onChange={(e) => setEstadoManual(e.target.value)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Incompleto</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="estado"
                      value="completado"
                      checked={estadoManual === 'completado'}
                      onChange={(e) => setEstadoManual(e.target.value)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Completado</span>
                  </label>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      setGuardando(true);
                      await updateStartupDataInReporte(reporte.id, startup.id, {
                        estado: estadoManual
                      });
                      // Actualizar el estado local de la startup
                      if (onUpdate) {
                        onUpdate({
                          ...startup,
                          estado: estadoManual
                        });
                      }
                      alert('Estado guardado correctamente');
                    } catch (error) {
                      console.error('Error guardando estado:', error);
                      alert('Error al guardar el estado: ' + (error.message || 'Error desconocido'));
                    } finally {
                      setGuardando(false);
                    }
                  }}
                  disabled={guardando}
                  className="ml-4"
                >
                  {guardando ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Estado
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Información Estática */}
          <Card>
            <CardHeader>
              <CardTitle>Información de la Startup</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                  <p className="text-base">{startup.nombre}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fundador</label>
                  <p className="text-base">{startup.fundador || 'No especificado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Arcángel</label>
                  <p className="text-base">{startup.arcangel || 'No especificado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Website</label>
                  <a href={startup.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {startup.website || 'No disponible'}
                  </a>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Descripción</label>
                <p className="text-base mt-1">{startup.descripcion || 'Sin descripción disponible.'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Datos para el informe público */}
          <Card>
            <CardHeader>
              <CardTitle>Datos para el informe público</CardTitle>
              <CardDescription>Incorporación, cifras, valoración, logo y resumen que se muestran en la tarjeta de cada participada.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Logo de la startup</label>
                {isEditing ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      placeholder="URL del logo (o subir imagen)"
                      value={formData.kpis.logo_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, logo_url: e.target.value } }))}
                      className="flex-1 min-w-[200px]"
                    />
                    <label className={cn(
                      "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-input bg-background h-9 px-3",
                      "hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                    )}>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f && f.type.startsWith('image/')) {
                            const reader = new FileReader();
                            reader.onload = () => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, logo_url: reader.result } }));
                            reader.readAsDataURL(f);
                          }
                          e.target.value = '';
                        }}
                      />
                      <ImagePlus className="h-4 w-4" />
                      Subir imagen
                    </label>
                  </div>
                ) : null}
                {(formData.kpis.logo_url || formData.kpis.logoUrl) && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={formData.kpis.logo_url || formData.kpis.logoUrl} alt="Logo" className="h-16 w-16 object-contain rounded border border-input bg-muted/30" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Incorporación a cartera</label>
                  {isEditing ? (
                    <Input value={formData.kpis.incorporation} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, incorporation: e.target.value } }))} placeholder="ej. Sep 24" />
                  ) : (
                    <p className="text-base">{formData.kpis.incorporation || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Total invertido</label>
                  {isEditing ? (
                    <Input value={formData.kpis.totalInvertido} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, totalInvertido: e.target.value } }))} placeholder="ej. €100k" />
                  ) : (
                    <p className="text-base">{formData.kpis.totalInvertido || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">% Portfolio</label>
                  {isEditing ? (
                    <Input value={formData.kpis.pctPortfolio} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, pctPortfolio: e.target.value } }))} placeholder="ej. 8,22%" />
                  ) : (
                    <p className="text-base">{formData.kpis.pctPortfolio || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">% Equity</label>
                  {isEditing ? (
                    <Input value={formData.kpis.pctEquity} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, pctEquity: e.target.value } }))} placeholder="ej. 1,68% o NC" />
                  ) : (
                    <p className="text-base">{formData.kpis.pctEquity || '—'}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Resumen para el informe</label>
                {isEditing ? (
                  <Textarea value={formData.kpis.summary} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, summary: e.target.value } }))} placeholder="Descripción breve que aparece en la tarjeta (si está vacío se usa la descripción de la startup)" rows={3} className="resize-y" />
                ) : (
                  <p className="text-base whitespace-pre-line">{formData.kpis.summary || startup.descripcion || '—'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Web (informe)</label>
                {isEditing ? (
                  <Input type="url" value={formData.kpis.web} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, web: e.target.value } }))} placeholder={startup.website || 'https://...'} />
                ) : (
                  <p className="text-base">{formData.kpis.web || startup.website || '—'}</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Valoración cualitativa</label>
                  {isEditing ? (
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.kpis.valoracion}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, valoracion: e.target.value } }))}
                    >
                      <option value="">—</option>
                      <option value="En línea con lo esperado">En línea con lo esperado</option>
                      <option value="Mejor de lo esperado">Mejor de lo esperado</option>
                      <option value="Exit">Exit</option>
                      <option value="Fallida">Fallida</option>
                    </select>
                  ) : (
                    <p className="text-base">{formData.kpis.valoracion || '—'}</p>
                  )}
                </div>
                {(formData.kpis.valoracion === 'Exit' || (!isEditing && formData.kpis.exitValue)) && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Valor exit (ej. 191.063 €)</label>
                    {isEditing ? (
                      <Input value={formData.kpis.exitValue} onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, exitValue: e.target.value } }))} placeholder="191.063 €" />
                    ) : (
                      <p className="text-base">{formData.kpis.exitValue || '—'}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* KPIs Financieros */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                KPIs Financieros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Ingresos</label>
                  {isEditing ? (
                    <Input
                      value={formData.kpis.ingresos}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, ingresos: e.target.value } }))}
                      placeholder="€0"
                    />
                  ) : (
                    <p className="text-xl font-semibold">{formData.kpis.ingresos || 'No disponible'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Gastos</label>
                  {isEditing ? (
                    <Input
                      value={formData.kpis.gastos}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, gastos: e.target.value } }))}
                      placeholder="€0"
                    />
                  ) : (
                    <p className="text-xl font-semibold">{formData.kpis.gastos || 'No disponible'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Empleados</label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={formData.kpis.empleados}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, empleados: e.target.value } }))}
                      placeholder="0"
                    />
                  ) : (
                    <p className="text-xl font-semibold">{formData.kpis.empleados || 'No disponible'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Runway (meses)</label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={formData.kpis.runway}
                      onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, runway: e.target.value } }))}
                      placeholder="0"
                    />
                  ) : (
                    <p className="text-xl font-semibold">
                      {formData.kpis.runway ? `${formData.kpis.runway} meses` : 'No disponible'}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="rondaAbierta"
                    checked={formData.kpis.rondaAbierta}
                    onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, rondaAbierta: e.target.checked } }))}
                    disabled={!isEditing}
                    className="h-4 w-4"
                  />
                  <label htmlFor="rondaAbierta" className="text-sm font-medium">Ronda de Financiación Abierta</label>
                </div>
                
                {formData.kpis.rondaAbierta && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1 block">Valoración</label>
                      {isEditing ? (
                        <Input
                          value={formData.kpis.valoracion}
                          onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, valoracion: e.target.value } }))}
                          placeholder="€0"
                        />
                      ) : (
                        <p className="text-sm">{formData.kpis.valoracion || 'No disponible'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1 block">Progreso</label>
                      {isEditing ? (
                        <Input
                          value={formData.kpis.progresoRonda}
                          onChange={(e) => setFormData(prev => ({ ...prev, kpis: { ...prev.kpis, progresoRonda: e.target.value } }))}
                          placeholder="0%"
                        />
                      ) : (
                        <p className="text-sm">{formData.kpis.progresoRonda || 'No disponible'}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Hitos del Trimestre */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Hitos del Trimestre
              </CardTitle>
              <CardDescription>
                {Object.values(formData.milestones).flat().filter(h => h.trim()).length} hitos relevantes del trimestre
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.keys(areasHitos).map((area) => {
                  const areaInfo = areasHitos[area];
                  const AreaIcon = areaInfo.icon;
                  const milestones = formData.milestones[area] || [];
                  
                  return (
                    <div key={area} className="border border-border/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <AreaIcon className={cn("h-5 w-5", areaInfo.color)} />
                          <h3 className="font-medium">{area}</h3>
                        </div>
                        {isEditing && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddMilestone(area)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Agregar
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {milestones.length === 0 && !isEditing ? (
                          <p className="text-sm text-muted-foreground italic">No hay hitos en esta categoría</p>
                        ) : (
                          milestones.map((hito, index) => (
                            <div key={index} className="flex items-start gap-2">
                              {isEditing ? (
                                <>
                                  <Textarea
                                    value={hito}
                                    onChange={(e) => handleMilestoneChange(area, index, e.target.value)}
                                    placeholder="Descripción del hito..."
                                    className="flex-1 min-h-[60px]"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveMilestone(area, index)}
                                    className="mt-1"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </>
                              ) : (
                                <div className="flex-1 p-3 rounded-lg border border-border/50 bg-muted/30">
                                  <p className="text-sm">{hito || <span className="text-muted-foreground italic">Hito vacío</span>}</p>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                        {isEditing && milestones.length === 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddMilestone(area)}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Agregar primer hito
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DatosStartup;
